import { translateIsoDateStringToHumanReadable } from "./translateIsoDateStringToHumanReadable"
import { AreaUnit, DistanceUnit } from "./units"

export { formatTimeAgo } from "./formatTimeAgo"
export { formatTimeRemaining } from "./formatTimeRemaining"

export type FormatProps<V, U> = {
    value: V
    unit?: U
    waterfall?: boolean
}

export { AreaUnit, DistanceUnit }

export enum DateFormat {
    MonthDayYear = "monthdayyear",
    DayMonthYear = "daymonthyear",
    YearDayMonth = "yeardaymonth",
    YearMonthDay = "yearmonthday",
    MMMDDYYYY = "MMMDDYYYY", // eg. Nov 24th, 2024
    MMMMDYYYY = "MMMMDYYYY", // eg. November 24, 2024
}

export enum TimeFormat {
    TwelveHour = "twelvehour",
    TwentyFourHour = "twentyfourhour",
}

function format(value: number, unit: string) {
    let valueString = value.toString()
    if (value < 0.01) {
        valueString = value.toPrecision(1)
    } else if (value < 1) {
        valueString = value.toFixed(2)
    } else if (value < 100) {
        valueString = value.toFixed(1)
    } else {
        valueString = value.toFixed(0)
    }
    return `${formatNumberWithCommas(valueString)} ${unit}`
}

export const formatDistance = ({ value, unit }: FormatProps<number, DistanceUnit>) => {
    if (unit === DistanceUnit.Meters || unit === DistanceUnit.Kilometers) {
        if (value === 0) {
            return format(0, "m")
        }
        if (value < 1) {
            return format(value * 100, "cm")
        } else if (value < 10000) {
            return format(value, "m")
        } else {
            return format(value / 1000, "km")
        }
    } else {
        const inches = value * 39.3701
        const feet = value * 3.28084
        const miles = value * 0.000621371
        if (feet === 0) {
            return format(0, "ft")
        }
        if (feet < 1) {
            return format(inches, "in")
        } else if (feet < 10000) {
            return format(feet, "ft")
        } else {
            return format(miles, "mi")
        }
    }
}

export const formatAltitude = ({ value, unit }: FormatProps<number, DistanceUnit>) => {
    if (unit === DistanceUnit.Meters || unit === DistanceUnit.Kilometers) {
        return format(value, "m")
    }
    return format(value * 3.28084, "ft")
}

export const formatCoordinate = ({
    lat,
    lng,
    numDecimals = 4,
}: {
    lat: number
    lng: number
    numDecimals?: number
}) => {
    return `${lat.toFixed(numDecimals)}°, ${lng.toFixed(numDecimals)}°`
}

export const mpsToMph = (mps: number) => mps * 2.23694
export const mpsToKph = (mps: number) => mps * 3.6

export const formatSpeed = ({ value, unit }: FormatProps<number, DistanceUnit>) => {
    if (unit === DistanceUnit.Meters || unit === DistanceUnit.Kilometers) {
        return format(mpsToKph(value), "km/h")
    }
    return format(mpsToMph(value), "mph")
}

export const getSpeedDisplay = (unit: DistanceUnit) => {
    const isMetric = unit === DistanceUnit.Meters || unit === DistanceUnit.Kilometers
    return {
        label: isMetric ? "km/h" : "mph",
        convert: isMetric ? mpsToKph : mpsToMph,
    }
}

export const formatArea = ({ value, unit, waterfall }: FormatProps<number, AreaUnit>) => {
    let v = value
    let u = ""
    if (unit === AreaUnit.SquareKilometers) {
        v = value / 1000000
        u = "km²"
    } else if (unit === AreaUnit.SquareMiles) {
        v = value / 2.59e6
        u = "mi²"
    } else if (unit === AreaUnit.Hectares) {
        v = value / 10000
        u = "ha"
    } else if (unit === AreaUnit.Acres) {
        //if the unit is specified to be Acres, we don't want to convert it to ft, so return immediately
        v = value / 4046.86
        u = "ac"
        if (!waterfall) return format(v, u)
    } else {
        v = value / 4046.86
        u = "ac"
    }
    if (v < 1) {
        if (unit === AreaUnit.SquareKilometers || unit === AreaUnit.Hectares) {
            v = value
            u = "m²"
        } else {
            v = value * 10.7639
            u = "ft²"
        }
    }

    return format(v, u)
}

export const formatDegrees = ({ value }: FormatProps<number, never>) => {
    return `${value.toFixed(6)}°`
}

export const formatShortDate = ({ value }: FormatProps<string | Date, never>) => {
    const dateString = parseDateSafely(value)
        .toISOString()
        .replace(/[-T:.Z]/g, "")
    const formattedDate = `${dateString.slice(0, 4)}/${dateString.slice(4, 6)}/${dateString.slice(
        6,
        8
    )}`
    return formattedDate
}

export const formatLongDate = ({
    value,
    dateFormat,
    timeFormat,
}: FormatProps<string | Date, never> & { dateFormat?: DateFormat; timeFormat?: TimeFormat }) => {
    const date = formatDate({ value, format: dateFormat })
    const time = formatTime({ value, format: timeFormat })
    const formattedDate = `${date}, ${time}`
    return formattedDate
}

/**
 * Good for names of file exports.
 * Formatted like: `YYMMDDHHmmss`
 */
export const createCondensedDate = () => {
    const date = new Date()
    const pieces = [
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
    ]
    return pieces.map((item) => item.toString().padStart(2, "0")).join("")
}

export const parseLocalDate = (value: string): Date => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
    if (!match) throw new Error("Could not parse local date " + value)

    const [, year, month, day] = match
    return new Date(Number(year), Number(month) - 1, Number(day))
}

export const parseDateSafely = (value: Date | string | number): Date => {
    if (value instanceof Date) {
        return value
    }

    const parsedDate = new Date(value)
    if (parsedDate instanceof Date && !isNaN(parsedDate.getTime())) {
        return parsedDate
    }

    throw new Error("Could not parse date " + value)
}

const relativeDateMap: Record<string, string> = {
    days_ago_0: "Today",
    days_ago_1: "A day",
    days_ago_7: "A few days",
    days_ago_14: "1 week",
    days_ago_21: "2 weeks",
    days_ago_28: "3 weeks",
    months_ago_1: "1 month",
    months_ago_2: "2 months",
    months_ago_3: "3 months",
    months_ago_many: "Many months",
}

const relativeDateTranslator = (key: string, options?: { count: number }) => {
    if (key === "days_from") {
        const count = options?.count ?? 0
        const unit = count === 1 ? "day" : "days"
        return `${count} ${unit} from today`
    }

    return relativeDateMap[key] ?? key
}

export const describeRelativeTime = (value: Date | string | number) => {
    const referenceDate = parseDateSafely(value)
    const now = new Date()
    const humanReadable =
        translateIsoDateStringToHumanReadable(
            relativeDateTranslator,
            referenceDate.toISOString(),
            now.toISOString()
        ) ?? ""

    if (!humanReadable) {
        return ""
    }

    if (referenceDate.getTime() > now.getTime()) {
        return humanReadable
    }

    const isToday = humanReadable === "Today"
    const timePortion = isToday
        ? `Today at ${formatTime({ value: referenceDate, format: TimeFormat.TwentyFourHour })}`
        : humanReadable

    return `${timePortion} ago`
}

const relativeTimeFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" })
const timeOfDayFormatter = new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
})

const isSameCalendarDay = (date: Date, now: Date) =>
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()

const formatTimeOfDay = (date: Date) =>
    timeOfDayFormatter.format(date).replace(/\s+/gu, "").toLowerCase()

const monthDayFormatter = new Intl.DateTimeFormat(undefined, { month: "long", day: "numeric" })

const formatWithinDay = (date: Date) => {
    const seconds = Math.round((date.getTime() - Date.now()) / 1000)
    const units: [Intl.RelativeTimeFormatUnit, number][] = [
        ["hour", 60 * 60],
        ["minute", 60],
    ]
    for (const [unit, secondsPerUnit] of units) {
        if (Math.abs(seconds) >= secondsPerUnit) {
            return relativeTimeFormatter.format(Math.round(seconds / secondsPerUnit), unit)
        }
    }
    return relativeTimeFormatter.format(seconds, "second")
}

export const formatRelativeTime = (value: Date | string | number) => {
    const date = parseDateSafely(value)
    if (Math.abs(Date.now() - date.getTime()) >= 60 * 60 * 24 * 1000) {
        return monthDayFormatter.format(date)
    }
    return formatWithinDay(date)
}

/**
 * Timestamp for a feed that already groups by calendar day: the day is carried
 * by the group heading, so only today's entries need a relative time.
 */
export const formatFeedTime = (value: Date | string | number) => {
    const date = parseDateSafely(value)
    if (!isSameCalendarDay(date, new Date())) return formatTimeOfDay(date)
    return formatWithinDay(date)
}

const dayLabelFormatter = new Intl.DateTimeFormat(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
})

/**
 * Calendar-day heading for grouped feeds: "Today", "Yesterday", or a full date.
 */
export const formatDayLabel = (value: Date | string | number) => {
    const date = parseDateSafely(value)
    const now = new Date()
    if (isSameCalendarDay(date, now)) return "Today"

    const yesterday = new Date(now)
    yesterday.setDate(yesterday.getDate() - 1)
    if (isSameCalendarDay(date, yesterday)) return "Yesterday"

    return dayLabelFormatter.format(date)
}

export type DaySection<T> = {
    label: string
    items: T[]
}

/**
 * Groups a feed into calendar-day sections headed by `formatDayLabel`.
 * Sections appear in first-seen order and a day is never repeated, so a
 * list that is not strictly date-ordered still yields one heading per day.
 */
export function groupByDay<T>(items: T[], dateOf: (item: T) => Date | string | number) {
    return items.reduce<DaySection<T>[]>((sections, item) => {
        const label = formatDayLabel(dateOf(item))
        const section = sections.find((candidate) => candidate.label === label)
        if (section) section.items.push(item)
        else sections.push({ label, items: [item] })
        return sections
    }, [])
}

export const formatDateRange = ({
    value: { start, end },
    dateFormat,
    timeFormat,
}: FormatProps<{ start: string | Date; end: string | Date }, never> & {
    dateFormat?: DateFormat
    timeFormat?: TimeFormat
}) => {
    const [startDate, endDate] = [
        formatLongDate({ value: parseDateSafely(start), dateFormat, timeFormat }),
        formatLongDate({ value: parseDateSafely(end), dateFormat, timeFormat }),
    ]

    return `${startDate} - ${endDate}`
}

export const makeDaySuffix = (day: number) => {
    if (day % 10 === 1 && day % 100 !== 11) return "st"
    if (day % 10 === 2 && day % 100 !== 12) return "nd"
    if (day % 10 === 3 && day % 100 !== 13) return "rd"
    return "th"
}

export const formatDate = ({
    value,
    format = DateFormat.YearMonthDay,
    omitSuffix = false,
}: {
    value: Date | string | number
    format?: DateFormat
    omitSuffix?: boolean
}): string => {
    const date = parseDateSafely(value)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0") // Add 1 to month since it's zero-based.
    const day = String(date.getDate()).padStart(2, "0")
    switch (format) {
        case DateFormat.YearMonthDay:
            return `${year}/${month}/${day}`
        case DateFormat.DayMonthYear:
            return `${day}/${month}/${year}`
        case DateFormat.MonthDayYear:
            return `${month}/${day}/${year}`
        case DateFormat.YearDayMonth:
            return `${year}/${day}/${month}`
        case DateFormat.MMMDDYYYY: {
            const month = date.toLocaleString("en-US", { month: "short" }) // eg. Nov
            const day = date.getDate()
            const suffix = omitSuffix ? "" : makeDaySuffix(day)
            return `${month} ${day}${suffix}, ${year}`
        }
        case DateFormat.MMMMDYYYY: {
            const month = date.toLocaleString("en-US", { month: "long" }) // eg. November
            const day = date.getDate()
            return `${month} ${day}, ${year}`
        }
        default:
            throw "invalid date format passed to formatDate"
    }
}

export function formatTime({
    value,
    format = TimeFormat.TwentyFourHour,
}: {
    value: Date | string
    format?: TimeFormat
}): string {
    const date = parseDateSafely(value)
    const hours = date.getHours()
    const minutes = date.getMinutes()

    switch (format) {
        case TimeFormat.TwelveHour: {
            const amPm = hours >= 12 ? "PM" : "AM"
            const formattedHours = hours % 12 || 12 // Convert to 12-hour format
            return `${formattedHours.toString().padStart(2, "0")}:${minutes
                .toString()
                .padStart(2, "0")} ${amPm}`
        }
        case TimeFormat.TwentyFourHour:
            return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
        default:
            throw new Error("Invalid time format used in formatTime")
    }
}

export function formatNumberWithCommas(value: string) {
    if (value.length < 4) return value

    const parts = value.split(".")
    let integerPart = parts[0]
    const decimalPart = parts[1] || ""

    let newNum = ""
    let count = 0

    if (integerPart.startsWith("-")) {
        newNum = "-"
        integerPart = integerPart.slice(1)
    }

    for (let i = integerPart.length - 1; i >= 0; i--) {
        newNum = integerPart[i] + newNum
        count++

        if (count % 3 === 0 && i !== 0) {
            newNum = "," + newNum
        }
    }

    return decimalPart ? newNum + "." + decimalPart : newNum
}

export const feetToMeters = (feet: number) => feet * 0.3048
export const metersToFeet = (meters: number) => meters / 0.3048

export const feetToYards = (feet: number) => feet / 3
export const feetToNM = (feet: number) => feet / 6076

export const yardsToMeters = (yards: number) => yards / 1.094
export const NMToMeters = (NM: number) => NM * 1852

export const metersToNM = (meters: number) => meters / 1852
export const metersToYards = (meters: number) => meters * 1.094

export const milesToKilometers = (miles: number) => miles * 1.60934
export const kilometersToMiles = (kilometers: number) => kilometers / 1.60934

export const feetToMiles = (feet: number) => feet / 5280
export const milesToFeet = (miles: number) => miles * 5280

export const metersToKilometers = (meters: number) => meters / 1000
export const kilometersToMeters = (kilometers: number) => kilometers * 1000

export const feetToKilometers = (feet: number) => metersToKilometers(feetToMeters(feet))
export const kilometersToFeet = (kilometers: number) => metersToFeet(kilometersToMeters(kilometers))

export const milesToMeters = (miles: number) => feetToMeters(milesToFeet(miles))
export const metersToMiles = (meters: number) => feetToMiles(metersToFeet(meters))

//a very generic helper function that takes a value, and the unit that it's in, and converts it to another unit
export const convertDistance = (
    value: number,
    startUnit: DistanceUnit,
    endUnit: DistanceUnit
): number => {
    switch (startUnit) {
        case DistanceUnit.Feet:
            switch (endUnit) {
                case DistanceUnit.Feet:
                    return value
                case DistanceUnit.Meters:
                    return feetToMeters(value)
                case DistanceUnit.Miles:
                    return feetToMiles(value)
                case DistanceUnit.Kilometers:
                    return feetToKilometers(value)
                case DistanceUnit.Yards:
                    return feetToYards(value)
                case DistanceUnit.NauticalMiles:
                    return feetToNM(value)
            }
        case DistanceUnit.Meters:
            switch (endUnit) {
                case DistanceUnit.Feet:
                    return metersToFeet(value)
                case DistanceUnit.Meters:
                    return value
                case DistanceUnit.Miles:
                    return metersToMiles(value)
                case DistanceUnit.Kilometers:
                    return metersToKilometers(value)
                case DistanceUnit.Yards:
                    return feetToYards(metersToFeet(value))
                case DistanceUnit.NauticalMiles:
                    return feetToNM(metersToFeet(value))
            }
        case DistanceUnit.Miles:
            switch (endUnit) {
                case DistanceUnit.Feet:
                    return milesToFeet(value)
                case DistanceUnit.Meters:
                    return milesToMeters(value)
                case DistanceUnit.Miles:
                    return value
                case DistanceUnit.Kilometers:
                    return milesToKilometers(value)
                case DistanceUnit.Yards:
                    return feetToYards(milesToFeet(value))
                case DistanceUnit.NauticalMiles:
                    return feetToNM(milesToFeet(value))
            }
        case DistanceUnit.Kilometers:
            switch (endUnit) {
                case DistanceUnit.Feet:
                    return kilometersToFeet(value)
                case DistanceUnit.Meters:
                    return kilometersToMeters(value)
                case DistanceUnit.Miles:
                    return kilometersToMiles(value)
                case DistanceUnit.Kilometers:
                    return value
                case DistanceUnit.Yards:
                    return feetToYards(kilometersToFeet(value))
                case DistanceUnit.NauticalMiles:
                    return feetToNM(kilometersToFeet(value))
            }
        case DistanceUnit.NauticalMiles:
            switch (endUnit) {
                case DistanceUnit.Feet:
                    return metersToFeet(NMToMeters(value))
                case DistanceUnit.Meters:
                    return NMToMeters(value)
                case DistanceUnit.Miles:
                    return metersToMiles(NMToMeters(value))
                case DistanceUnit.Kilometers:
                    return metersToKilometers(NMToMeters(value))
                case DistanceUnit.Yards:
                    return metersToYards(NMToMeters(value))
                case DistanceUnit.NauticalMiles:
                    return value
            }
        case DistanceUnit.Yards:
            switch (endUnit) {
                case DistanceUnit.Feet:
                    return metersToFeet(yardsToMeters(value))
                case DistanceUnit.Meters:
                    return yardsToMeters(value)
                case DistanceUnit.Miles:
                    return metersToMiles(yardsToMeters(value))
                case DistanceUnit.Kilometers:
                    return metersToKilometers(yardsToMeters(value))
                case DistanceUnit.Yards:
                    return value
                case DistanceUnit.NauticalMiles:
                    return metersToNM(yardsToMeters(value))
            }
    }
}

export const sqfeetTosqMeters = (sqfeet: number) => sqfeet / 10.764
export const sqmetersTosqFeet = (sqmeters: number) => sqmeters * 10.764

export const sqfeetTosqMiles = (sqfeet: number) => sqfeet / 2.788e7
export const sqmilesTosqFeet = (sqmiles: number) => sqmiles * 2.788e7

export const sqfeetTosqKilometers = (sqfeet: number) => sqfeet / 1.076e7
export const sqkilometersTosqFeet = (sqkm: number) => sqkm * 1.076e7

export const sqfeetToAcres = (sqfeet: number) => sqfeet / 43560
export const acresTosqFeet = (acres: number) => acres * 43560

export const sqfeetToHectares = (sqfeet: number) => sqfeet / 107600
export const hectaresTosqFeet = (hectares: number) => hectares * 107600

//a very generic helper function that takes a value, and the unit that it's in, and converts it to another unit
export const convertArea = (value: number, startUnit: AreaUnit, endUnit: AreaUnit): number => {
    switch (startUnit) {
        case AreaUnit.SquareFeet:
            switch (endUnit) {
                case AreaUnit.SquareFeet:
                    return value
                case AreaUnit.SquareMeters:
                    return sqfeetTosqMeters(value)
                case AreaUnit.SquareMiles:
                    return sqfeetTosqMiles(value)
                case AreaUnit.SquareKilometers:
                    return sqfeetTosqKilometers(value)
                case AreaUnit.Acres:
                    return sqfeetToAcres(value)
                case AreaUnit.Hectares:
                    return sqfeetToHectares(value)
            }

        case AreaUnit.SquareMeters:
            switch (endUnit) {
                case AreaUnit.SquareFeet:
                    return sqmetersTosqFeet(value)
                case AreaUnit.SquareMeters:
                    return value
                case AreaUnit.SquareMiles:
                    return sqfeetTosqMiles(sqmetersTosqFeet(value))
                case AreaUnit.SquareKilometers:
                    return sqfeetTosqKilometers(sqmetersTosqFeet(value))
                case AreaUnit.Acres:
                    return sqfeetToAcres(sqmetersTosqFeet(value))
                case AreaUnit.Hectares:
                    return sqfeetToHectares(sqmetersTosqFeet(value))
            }
        case AreaUnit.SquareKilometers:
            switch (endUnit) {
                case AreaUnit.SquareFeet:
                    return sqkilometersTosqFeet(value)
                case AreaUnit.SquareMeters:
                    return sqfeetTosqMeters(sqkilometersTosqFeet(value))
                case AreaUnit.SquareMiles:
                    return sqfeetTosqMiles(sqkilometersTosqFeet(value))
                case AreaUnit.SquareKilometers:
                    return value
                case AreaUnit.Acres:
                    return sqfeetToAcres(sqkilometersTosqFeet(value))
                case AreaUnit.Hectares:
                    return sqfeetToHectares(sqkilometersTosqFeet(value))
            }
        case AreaUnit.SquareMiles:
            switch (endUnit) {
                case AreaUnit.SquareFeet:
                    return sqmilesTosqFeet(value)
                case AreaUnit.SquareMeters:
                    return sqfeetTosqMeters(sqmilesTosqFeet(value))
                case AreaUnit.SquareMiles:
                    return value
                case AreaUnit.SquareKilometers:
                    return sqfeetTosqKilometers(sqmilesTosqFeet(value))
                case AreaUnit.Acres:
                    return sqfeetToAcres(sqmilesTosqFeet(value))
                case AreaUnit.Hectares:
                    return sqfeetToHectares(sqmilesTosqFeet(value))
            }
        case AreaUnit.Acres:
            switch (endUnit) {
                case AreaUnit.SquareFeet:
                    return acresTosqFeet(value)
                case AreaUnit.SquareMeters:
                    return sqfeetTosqMeters(acresTosqFeet(value))
                case AreaUnit.SquareMiles:
                    return sqfeetTosqMiles(acresTosqFeet(value))
                case AreaUnit.SquareKilometers:
                    return sqfeetTosqKilometers(acresTosqFeet(value))
                case AreaUnit.Acres:
                    return value
                case AreaUnit.Hectares:
                    return sqfeetToHectares(acresTosqFeet(value))
            }
        case AreaUnit.Hectares:
            switch (endUnit) {
                case AreaUnit.SquareFeet:
                    return hectaresTosqFeet(value)
                case AreaUnit.SquareMeters:
                    return sqfeetTosqMeters(hectaresTosqFeet(value))
                case AreaUnit.SquareMiles:
                    return sqfeetTosqMiles(hectaresTosqFeet(value))
                case AreaUnit.SquareKilometers:
                    return sqfeetTosqKilometers(hectaresTosqFeet(value))
                case AreaUnit.Acres:
                    return sqfeetToAcres(hectaresTosqFeet(value))
                case AreaUnit.Hectares:
                    return value
            }
    }
}

export const formatHoursMinutesSecondsSinceDate = (rawDate: Date | number | string) => {
    const date = parseDateSafely(rawDate).getTime()
    const now = Date.now()

    const rawSeconds = Math.round((now - date) / 1000)
    const h = Math.floor(rawSeconds / 3600)
    const m = Math.floor((rawSeconds % 3600) / 60)
    const s = rawSeconds % 60
    return `${h ? `${h}:` : ""}${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
}

export const formatHoursMinutesSecondsUntilDate = (rawDate: Date | number | string) => {
    const date = parseDateSafely(rawDate).getTime()
    const now = Date.now()

    const rawSeconds = Math.round((date - now) / 1000)
    const h = Math.floor(rawSeconds / 3600)
    const m = Math.floor((rawSeconds % 3600) / 60)
    const s = rawSeconds % 60
    return `${h ? `${h}:` : ""}${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
}

export function formatBytes(sizeInBytes: number, useKBs?: boolean): string {
    const KB = 1024 // 1 KB in bytes
    const MB = 1024 * KB // 1 MB in bytes
    const GB = 1024 * MB // 1 GB in bytes
    const TB = 1024 * GB // 1 TB in bytes

    if (sizeInBytes < MB && useKBs) {
        const sizeInKB = sizeInBytes / KB
        return `${sizeInKB.toFixed(2)} KB`
    } else if (sizeInBytes < GB) {
        const sizeInMB = sizeInBytes / MB
        return `${sizeInMB.toFixed(2)} MB`
    } else if (sizeInBytes < TB) {
        const sizeInGB = sizeInBytes / GB
        return `${sizeInGB.toFixed(2)} GB`
    } else {
        const sizeInTB = sizeInBytes / TB
        return `${sizeInTB.toFixed(2)} TB`
    }
}
